import React, { useState, useEffect } from 'react';
import { RefreshCwIcon, CheckCircleIcon, AlertCircleIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';

interface MarketItem {
  name: string;
  price: number;
  unit: string;
  location?: string;
  date?: string;
}

interface MarketData {
  eggs: { items: MarketItem[]; source: string };
  milk: { items: MarketItem[]; source: string };
  meat: { items: MarketItem[]; source: string };
  vegetables: { items: MarketItem[]; source: string };
  fruits: { items: MarketItem[]; source: string };
  grains: { items: MarketItem[]; source: string };
  livestock: { items: MarketItem[]; source: string };
  source?: string;
  isRealData?: boolean;
  lastUpdated?: string;
}

const SimpleMarketPanel: React.FC = () => {
  const [marketData, setMarketData] = useState<MarketData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  const toggleSection = (sectionKey: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  // Helper function to organize data into table rows
  const getTableRows = (items: MarketItem[]) => {
    const itemsByName: Record<string, MarketItem[]> = {};
    
    // Group items by name
    items.forEach(item => {
      const key = item.name;
      if (!itemsByName[key]) itemsByName[key] = [];
      itemsByName[key].push(item);
    });
    
    // Create table rows
    return Object.entries(itemsByName).map(([itemName, itemData]) => {
      const row = {
        itemName,
        unit: itemData[0]?.unit || '',
        maseru: 'M0.00',
        mafeteng: 'M0.00',
        leribe: 'M0.00',
        berea: 'M0.00',
        butha: 'M0.00',
        mohales: 'M0.00',
        quthing: 'M0.00',
        qachas: 'M0.00',
        mokhotlong: 'M0.00',
        thaba: 'M0.00'
      };
      
      // Fill in actual prices where available
      itemData.forEach(item => {
        const location = item.location?.toLowerCase() || '';
        const price = `M${item.price.toFixed(2)}`;
        
        if (location.includes('maseru')) row.maseru = price;
        else if (location.includes('mafeteng')) row.mafeteng = price;
        else if (location.includes('leribe')) row.leribe = price;
        else if (location.includes('berea')) row.berea = price;
        else if (location.includes('butha')) row.butha = price;
        else if (location.includes('mohale')) row.mohales = price;
        else if (location.includes('quthing')) row.quthing = price;
        else if (location.includes('qacha')) row.qachas = price;
        else if (location.includes('mokhotlong')) row.mokhotlong = price;
        else if (location.includes('thaba')) row.thaba = price;
        else {
          // If no specific location, distribute across available slots
          const slots = ['maseru', 'mafeteng', 'leribe', 'berea', 'butha'];
          for (const slot of slots) {
            if (row[slot as keyof typeof row] === 'M0.00') {
              (row as any)[slot] = price;
              break;
            }
          }
        }
      });
      
      return row;
    });
  };

  const loadMarketData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('http://localhost:5000/api/market-prices');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Market data loaded:', data);
      setMarketData(data);
    } catch (err) {
      console.error('Market data error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
      
      // Use hardcoded fallback data
      setMarketData({
        eggs: {
          items: [
            { name: 'Large Eggs', price: 45.50, unit: 'per dozen', location: 'Maseru' },
            { name: 'Medium Eggs', price: 38.20, unit: 'per dozen', location: 'Mafeteng' }
          ],
          source: 'Fallback Data'
        },
        milk: {
          items: [
            { name: 'Fresh Milk', price: 18.75, unit: 'per litre', location: 'Maseru' }
          ],
          source: 'Fallback Data'
        },
        meat: {
          items: [
            { name: 'Beef', price: 85.40, unit: 'per kg', location: 'Maseru' },
            { name: 'Chicken', price: 42.60, unit: 'per kg', location: 'Mafeteng' }
          ],
          source: 'Fallback Data'
        },
        vegetables: {
          items: [
            { name: 'Potatoes', price: 12.30, unit: 'per kg', location: 'Maseru' },
            { name: 'Tomatoes', price: 15.80, unit: 'per kg', location: 'Mafeteng' }
          ],
          source: 'Fallback Data'
        },
        fruits: {
          items: [
            { name: 'Apples', price: 25.60, unit: 'per kg', location: 'Maseru' }
          ],
          source: 'Fallback Data'
        },
        grains: {
          items: [
            { name: 'White Maize', price: 8.50, unit: 'per kg', location: 'Maseru' },
            { name: 'Wheat', price: 9.40, unit: 'per kg', location: 'Leribe' }
          ],
          source: 'Fallback Data'
        },
        livestock: {
          items: [
            { name: 'Cattle', price: 4500.00, unit: 'per head', location: 'Maseru' }
          ],
          source: 'Fallback Data'
        },
        source: 'SADPII + Multiple Sources (Fallback)',
        isRealData: false,
        lastUpdated: new Date().toISOString()
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMarketData();
  }, []);

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
        <p>Loading Lesotho market data...</p>
      </div>
    );
  }

  if (error && !marketData) {
    return (
      <div className="p-6 text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-2xl">❌</span>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to Load Market Data</h3>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={loadMarketData}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  const sections = [
    { key: 'eggs', title: 'Eggs', icon: '🥚' },
    { key: 'milk', title: 'Milk', icon: '🥛' },
    { key: 'meat', title: 'Meat', icon: '🥩' },
    { key: 'vegetables', title: 'Vegetables', icon: '🥬' },
    { key: 'fruits', title: 'Fruits', icon: '🍎' },
    { key: 'grains', title: 'Grains', icon: '🌾' },
    { key: 'livestock', title: 'Livestock', icon: '🐄' }
  ];

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <span className="text-xl">🇱🇸</span>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Lesotho Market Prices</h2>
                <div className="flex items-center space-x-2">
                  {marketData?.isRealData ? (
                    <CheckCircleIcon className="w-4 h-4 text-green-500" />
                  ) : (
                    <AlertCircleIcon className="w-4 h-4 text-orange-500" />
                  )}
                  <p className="text-sm text-gray-600">
                    {marketData?.source || 'SADPII + Multiple Sources'}
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={loadMarketData}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              <RefreshCwIcon className="w-4 h-4" />
              <span>Refresh</span>
            </button>
          </div>
          {marketData?.lastUpdated && (
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-gray-500">
                Last updated: {new Date(marketData.lastUpdated).toLocaleString()}
              </p>
              {marketData.isRealData ? (
                <span className="text-xs text-green-600 font-medium">✅ Real Data</span>
              ) : (
                <span className="text-xs text-orange-600 font-medium">📊 Fallback Data</span>
              )}
            </div>
          )}
        </div>

        {/* Market Data - Accordion */}
        <div className="divide-y divide-gray-200">
          {sections.map((section) => {
            const category = marketData?.[section.key as keyof MarketData] as any;
            if (!category || !category.items || category.items.length === 0) {
              return null;
            }

            const isExpanded = expandedSections[section.key];

            return (
              <div key={section.key} className="">
                {/* Accordion Header */}
                <button
                  onClick={() => toggleSection(section.key)}
                  className="w-full px-4 py-4 flex items-center justify-between text-left hover:bg-green-50 transition-colors focus:outline-none focus:bg-green-50"
                >
                  <div className="flex items-center space-x-3">
                    <span className="text-xl">{section.icon}</span>
                    <div>
                      <h3 className="font-semibold text-gray-900 text-base sm:text-lg">{section.title}</h3>
                      <p className="text-xs sm:text-sm text-green-600 font-medium">
                        {category.items.length} items • Best: M{Math.min(...category.items.map((item: MarketItem) => item.price)).toFixed(2)}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-500 hidden sm:inline">
                      {isExpanded ? 'Hide' : 'Show'} prices
                    </span>
                    {isExpanded ? (
                      <ChevronUpIcon className="w-5 h-5 text-green-600" />
                    ) : (
                      <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Accordion Content - Table Format */}
                {isExpanded && (
                  <div className="px-4 pb-4 bg-green-50/30">
                    <div className="overflow-x-auto">
                      <table className="w-full min-w-full bg-white rounded-lg shadow-sm border border-green-100">
                        <thead className="bg-green-100">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-semibold text-green-800 border-b border-green-200">
                              Item
                            </th>
                            <th className="px-1 py-2 text-center text-xs font-semibold text-green-800 border-b border-green-200">
                              Maseru
                            </th>
                            <th className="px-1 py-2 text-center text-xs font-semibold text-green-800 border-b border-green-200">
                              Mafeteng
                            </th>
                            <th className="px-1 py-2 text-center text-xs font-semibold text-green-800 border-b border-green-200">
                              Leribe
                            </th>
                            <th className="px-1 py-2 text-center text-xs font-semibold text-green-800 border-b border-green-200">
                              Berea
                            </th>
                            <th className="px-1 py-2 text-center text-xs font-semibold text-green-800 border-b border-green-200">
                              Butha-Buthe
                            </th>
                            <th className="px-1 py-2 text-center text-xs font-semibold text-green-800 border-b border-green-200">
                              Mohale's Hoek
                            </th>
                            <th className="px-1 py-2 text-center text-xs font-semibold text-green-800 border-b border-green-200">
                              Quthing
                            </th>
                            <th className="px-1 py-2 text-center text-xs font-semibold text-green-800 border-b border-green-200">
                              Qacha's Nek
                            </th>
                            <th className="px-1 py-2 text-center text-xs font-semibold text-green-800 border-b border-green-200">
                              Mokhotlong
                            </th>
                            <th className="px-1 py-2 text-center text-xs font-semibold text-green-800 border-b border-green-200">
                              Thaba-Tseka
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {getTableRows(category.items).map((row, index) => (
                            <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-green-50/50'}>
                              <td className="px-3 py-2 text-xs sm:text-sm font-medium text-gray-900 border-b border-gray-100">
                                <div>
                                  <p className="font-semibold">{row.itemName}</p>
                                  <p className="text-xs text-gray-600">{row.unit}</p>
                                </div>
                              </td>
                              <td className="px-1 py-2 text-center text-xs border-b border-gray-100">
                                <span className={`font-semibold ${row.maseru === 'M0.00' ? 'text-gray-400' : 'text-green-700'}`}>
                                  {row.maseru}
                                </span>
                              </td>
                              <td className="px-1 py-2 text-center text-xs border-b border-gray-100">
                                <span className={`font-semibold ${row.mafeteng === 'M0.00' ? 'text-gray-400' : 'text-green-700'}`}>
                                  {row.mafeteng}
                                </span>
                              </td>
                              <td className="px-1 py-2 text-center text-xs border-b border-gray-100">
                                <span className={`font-semibold ${row.leribe === 'M0.00' ? 'text-gray-400' : 'text-green-700'}`}>
                                  {row.leribe}
                                </span>
                              </td>
                              <td className="px-1 py-2 text-center text-xs border-b border-gray-100">
                                <span className={`font-semibold ${row.berea === 'M0.00' ? 'text-gray-400' : 'text-green-700'}`}>
                                  {row.berea}
                                </span>
                              </td>
                              <td className="px-1 py-2 text-center text-xs border-b border-gray-100">
                                <span className={`font-semibold ${row.butha === 'M0.00' ? 'text-gray-400' : 'text-green-700'}`}>
                                  {row.butha}
                                </span>
                              </td>
                              <td className="px-1 py-2 text-center text-xs border-b border-gray-100">
                                <span className={`font-semibold ${row.mohales === 'M0.00' ? 'text-gray-400' : 'text-green-700'}`}>
                                  {row.mohales}
                                </span>
                              </td>
                              <td className="px-1 py-2 text-center text-xs border-b border-gray-100">
                                <span className={`font-semibold ${row.quthing === 'M0.00' ? 'text-gray-400' : 'text-green-700'}`}>
                                  {row.quthing}
                                </span>
                              </td>
                              <td className="px-1 py-2 text-center text-xs border-b border-gray-100">
                                <span className={`font-semibold ${row.qachas === 'M0.00' ? 'text-gray-400' : 'text-green-700'}`}>
                                  {row.qachas}
                                </span>
                              </td>
                              <td className="px-1 py-2 text-center text-xs border-b border-gray-100">
                                <span className={`font-semibold ${row.mokhotlong === 'M0.00' ? 'text-gray-400' : 'text-green-700'}`}>
                                  {row.mokhotlong}
                                </span>
                              </td>
                              <td className="px-1 py-2 text-center text-xs border-b border-gray-100">
                                <span className={`font-semibold ${row.thaba === 'M0.00' ? 'text-gray-400' : 'text-green-700'}`}>
                                  {row.thaba}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-4 pt-3 border-t border-green-200">
                      <p className="text-xs sm:text-sm text-green-700 font-medium">
                        📊 Source: {category.source} • Updated: {new Date().toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SimpleMarketPanel;